export const BPM_MATCH_TOLERANCE = 0.08;
export const BPM_SHORT_FADE_MAX = 0.20;

export const STRATEGY = Object.freeze({
  MATCHED_16: "matched16bar",
  SHORT_4: "short4bar",
  CUT: "cutOnDownbeat",
});

export const STRATEGY_LABELS = {
  [STRATEGY.MATCHED_16]: "16-bar matched",
  [STRATEGY.SHORT_4]: "4-bar short",
  [STRATEGY.CUT]: "Cut on downbeat",
};

export const chooseStrategy = (outBpm, inBpm) => {
  if (!outBpm || !inBpm) return STRATEGY.SHORT_4;
  const delta = Math.abs(inBpm - outBpm) / outBpm;
  if (delta <= BPM_MATCH_TOLERANCE) return STRATEGY.MATCHED_16;
  if (delta <= BPM_SHORT_FADE_MAX) return STRATEGY.SHORT_4;
  return STRATEGY.CUT;
};

const camelotParts = (slot) => {
  if (!slot) return null;
  const m = String(slot).match(/^(\d{1,2})([AB])$/);
  if (!m) return null;
  return { num: parseInt(m[1], 10), letter: m[2] };
};

const camelotDistance = (a, b) => {
  const pa = camelotParts(a);
  const pb = camelotParts(b);
  if (!pa || !pb) return null;
  const rawNum = Math.abs(pa.num - pb.num);
  const numDiff = Math.min(rawNum, 12 - rawNum);
  const letterDiff = pa.letter === pb.letter ? 0 : 1;
  return { numDiff, letterDiff };
};

export const camelotFit = (a, b) => {
  const d = camelotDistance(a, b);
  if (!d) return 0.5;
  if (d.numDiff === 0 && d.letterDiff === 0) return 1.0;
  if (d.numDiff === 0 && d.letterDiff === 1) return 0.85;
  if (d.numDiff === 1 && d.letterDiff === 0) return 0.85;
  if (d.numDiff === 1 && d.letterDiff === 1) return 0.55;
  if (d.numDiff === 2 && d.letterDiff === 0) return 0.45;
  return Math.max(0, 0.4 - 0.08 * d.numDiff);
};

export const bpmFit = (a, b) => {
  if (!a || !b) return 0;
  const delta = Math.abs(a - b) / a;
  return Math.exp(-((delta / 0.04) ** 2));
};

export const energyFit = (target, candidate) => {
  if (target == null || candidate == null) return 0.5;
  const diff = Math.abs(target - candidate);
  return Math.max(0, 1 - diff * 5);
};

export const computeAutoArc = ({ hourOfDay = 20, queueProgress = 0 } = {}) => {
  let baseTarget;
  if (hourOfDay < 12) baseTarget = 0.18;
  else if (hourOfDay < 18) baseTarget = 0.25;
  else if (hourOfDay < 22) baseTarget = 0.35;
  else baseTarget = 0.4;

  let arcMultiplier;
  if (queueProgress < 0.3) arcMultiplier = 0.85;
  else if (queueProgress < 0.7) arcMultiplier = 1.15;
  else arcMultiplier = 0.9;

  return baseTarget * arcMultiplier;
};

const W_BPM = 0.45;
const W_KEY = 0.35;
const W_ENERGY = 0.20;

export const scoreCandidate = (current, candidateFeatures, energyTarget) => {
  const bpmS = bpmFit(current.bpm, candidateFeatures.bpm);
  const keyS =
    current.camelot && candidateFeatures.camelot
      ? camelotFit(current.camelot, candidateFeatures.camelot)
      : 0.5;
  const target = energyTarget ?? current.energy ?? null;
  const energyS = energyFit(target, candidateFeatures.energy);
  const score = W_BPM * bpmS + W_KEY * keyS + W_ENERGY * energyS;
  return { score, bpmS, keyS, energyS };
};

export const pickNextTrack = (current, pool, featureCacheMap, options = {}) => {
  if (!pool.length) return null;
  if (!current) return { track: pool[0], index: 0 };

  const energyTarget = options.energyTarget ?? null;
  let best = null;

  for (let i = 0; i < pool.length; i++) {
    const candidate = pool[i];
    const features = featureCacheMap?.get(candidate.mediaId);
    if (!features || !features.bpm) continue;
    if (current.bpm && features.bpm) {
      const delta = Math.abs(features.bpm - current.bpm) / current.bpm;
      if (delta > BPM_MATCH_TOLERANCE) continue;
    }
    const breakdown = scoreCandidate(current, features, energyTarget);
    if (!best || breakdown.score > best.score) {
      best = {
        track: candidate,
        index: i,
        score: breakdown.score,
        breakdown,
        candidateFeatures: features,
      };
    }
  }

  if (best) return best;

  for (let i = 0; i < pool.length; i++) {
    const candidate = pool[i];
    const features = featureCacheMap?.get(candidate.mediaId);
    if (!features || !features.bpm) continue;
    return { track: candidate, index: i, score: 0, breakdown: null, candidateFeatures: features };
  }

  return { track: pool[0], index: 0 };
};
